import React, { useState, useEffect, useRef } from "react";
import { NavLink, useNavigate, Link } from "react-router-dom";
import {
  LayoutDashboard, Users, CalendarCheck, IdCard, Settings as SettingsIcon,
  Dumbbell, LogOut, Menu, X, Sparkles, SlidersHorizontal, Sun, Moon, ScanLine,
  PanelLeftClose, PanelLeftOpen, CalendarDays, Wand2, ListChecks, LogIn, ShieldCheck,
  TrendingUp, Heart, AlertTriangle, MessageSquare,
} from "lucide-react";
import { useAuth } from "../lib/auth";
import { useTheme } from "../lib/theme";
import { useLoginPrompt } from "../lib/loginPrompt";
import { endpoints } from "../lib/api";
import { toast } from "sonner";
import DemoBanner from "./DemoBanner";

/**
 * Workflow-grouped navigation.
 * Each item carries a `tone` token (lib/tokens.js semantic palette) so the
 * icon background subtly reinforces the business meaning (leads=amber,
 * trials=orange, etc) — Linear/Stripe style.
 */
const navSections = [
  {
    label: null,
    items: [
      { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard", tone: "ops" },
      { to: "/tasks", label: "Tasks", icon: ListChecks, testid: "nav-tasks", tone: "tasks", accent: true },
    ],
  },
  {
    label: "Grow",
    items: [
      { to: "/leads", label: "Leads", icon: Users, testid: "nav-leads", tone: "leads" },
      { to: "/trials", label: "Trials", icon: CalendarCheck, testid: "nav-trials", tone: "trials" },
    ],
  },
  {
    label: "Retain",
    items: [
      { to: "/members", label: "Members", icon: IdCard, testid: "nav-members", tone: "growth" },
      { to: "/members?view=renewals", label: "Renewals", icon: TrendingUp, testid: "nav-renewals", tone: "growth" },
      { to: "/members?view=at_risk", label: "At Risk", icon: AlertTriangle, testid: "nav-at-risk", tone: "risk" },
    ],
  },
  {
    label: "Operate",
    items: [
      { to: "/calendar", label: "Calendar", icon: CalendarDays, testid: "nav-calendar", tone: "ops" },
      { to: "/checkin", label: "Check-ins", icon: ScanLine, testid: "nav-checkin", tone: "ops" },
      { to: "/automations", label: "Automations", icon: SlidersHorizontal, testid: "nav-automations", tone: "ops" },
    ],
  },
  {
    label: "AI Coach",
    items: [
      { to: "/ai", label: "AI Insights", icon: Wand2, testid: "nav-ai", tone: "ai" },
      { to: "/ai?view=plans", label: "Plans", icon: Sparkles, testid: "nav-ai-plans", tone: "ai" },
    ],
  },
];

// Flat list (used by mobile bottom-bar that only shows top 4)
const mobileNav = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/tasks", label: "Tasks", icon: ListChecks, testid: "nav-tasks" },
  { to: "/leads", label: "Leads", icon: Users, testid: "nav-leads" },
  { to: "/members", label: "Members", icon: IdCard, testid: "nav-members" },
];

const SIDEBAR_KEY = "sidebar_collapsed";

export default function Layout({ children }) {
  const { user, logout, isDemo } = useAuth();
  const { theme, toggle: toggleTheme } = useTheme();
  const { requireLogin } = useLoginPrompt();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(SIDEBAR_KEY) === "1"; } catch { return false; }
  });
  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef(null);

  useEffect(() => {
    try { localStorage.setItem(SIDEBAR_KEY, collapsed ? "1" : "0"); } catch {}
  }, [collapsed]);

  useEffect(() => {
    const onClick = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) setProfileOpen(false);
    };
    if (profileOpen) document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [profileOpen]);

  const seedDemo = async () => {
    setSeeding(true);
    try {
      await endpoints.seed();
      toast.success("Demo data loaded", { description: "10 leads and 8 members added." });
      setTimeout(() => window.location.reload(), 400);
    } catch {
      toast.error("Could not load demo data");
    } finally { setSeeding(false); }
  };

  // Hide the "Load demo data" button once the gym already has real data
  // (any leads or members of their own). Keeps the UI clean for active gyms.
  const [hasRealData, setHasRealData] = useState(false);
  useEffect(() => {
    if (isDemo || !user) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await endpoints.dashboard();
        const s = data?.stats || {};
        const total = (s.active_members || 0) + (s.total_leads_month || 0) + (s.expiring_soon || 0) + (s.at_risk || 0);
        if (!cancelled && total > 0) setHasRealData(true);
      } catch {}
    })();
    return () => { cancelled = true; };
  }, [isDemo, user]);
  const showDemoLoader = !hasRealData;

  // Map tone token → Tailwind classes for the icon's tinted square.
  // Kept inline so tree-shaking keeps all referenced classes in the build.
  const TONE_ICON = {
    growth: "text-growth bg-growth/10",
    leads:  "text-leads  bg-leads/10",
    trials: "text-trials bg-trials/10",
    tasks:  "text-tasks  bg-tasks/10",
    ai:     "text-ai     bg-ai/10",
    risk:   "text-risk   bg-risk/10",
    ops:    "text-muted-foreground bg-muted",
  };

  const NavItem = ({ to, label, icon: Icon, testid, accent, tone = "ops" }) => (
    <NavLink
      to={to}
      data-testid={testid}
      title={collapsed ? label : undefined}
      className={({ isActive }) =>
        `group flex items-center gap-3 rounded-lg text-[13.5px] font-medium transition-all ${
          collapsed ? "justify-center px-0 py-2" : "px-2.5 py-2"
        } ${
          isActive
            ? accent
              ? "bg-tasks/12 text-tasks"
              : "bg-foreground/[0.06] text-foreground"
            : "text-muted-foreground hover:bg-foreground/[0.04] hover:text-foreground"
        }`
      }
    >
      {({ isActive }) => (
        <>
          <span
            className={`flex items-center justify-center w-7 h-7 rounded-md shrink-0 transition-colors ${
              isActive
                ? (accent ? "bg-tasks text-white" : `${TONE_ICON[tone]} ring-1 ring-inset ring-foreground/5`)
                : `${TONE_ICON[tone]} group-hover:ring-1 group-hover:ring-inset group-hover:ring-foreground/5`
            }`}
          >
            <Icon className="w-3.5 h-3.5" strokeWidth={2.2} />
          </span>
          {!collapsed && <span className="truncate">{label}</span>}
        </>
      )}
    </NavLink>
  );

  const FooterButton = ({ icon: Icon, label, onClick, testid, tone, disabled }) => (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      title={collapsed ? label : undefined}
      className={`w-full flex items-center gap-2 rounded-lg text-xs font-medium transition-colors ${
        collapsed ? "justify-center px-0 py-2" : "px-3 py-2"
      } ${
        tone === "danger"
          ? "text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/30"
          : "text-muted-foreground hover:bg-muted/50 hover:text-foreground"
      }`}
    >
      <Icon className="w-3.5 h-3.5 shrink-0" />
      {!collapsed && <span>{label}</span>}
    </button>
  );

  const FooterLink = ({ to, icon: Icon, label, testid }) => (
    <NavLink
      to={to}
      data-testid={testid}
      title={collapsed ? label : undefined}
      className={({ isActive }) =>
        `w-full flex items-center gap-2 rounded-lg text-xs font-medium transition-colors ${
          collapsed ? "justify-center px-0 py-2" : "px-3 py-2"
        } ${
          isActive
            ? "bg-muted text-foreground"
            : "text-muted-foreground hover:bg-muted/50 hover:text-foreground"
        }`
      }
    >
      <Icon className="w-3.5 h-3.5 shrink-0" />
      {!collapsed && <span>{label}</span>}
    </NavLink>
  );

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {isDemo && <DemoBanner />}
      <div className="flex-1 flex flex-col lg:flex-row">
      {/* Sidebar - desktop */}
      <aside
        className={`hidden lg:flex lg:flex-col lg:sticky lg:top-0 lg:h-screen border-r border-border/60 bg-sidebar transition-[width] duration-200 ${
          collapsed ? "lg:w-[68px]" : "lg:w-[244px]"
        }`}
      >
        <div className={`h-16 flex items-center border-b border-border ${collapsed ? "justify-center px-2" : "justify-between px-4"}`}>
          <Link to="/dashboard" className="flex items-center min-w-0">
            {collapsed ? (
              <span
                data-testid="sidebar-logo-collapsed"
                className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-primary text-primary-foreground font-display font-black text-base leading-none"
              >
                F<span className="text-foreground/90 dark:text-white">.</span>
              </span>
            ) : (
              <>
                <img
                  src="/logo-light.png"
                  alt="Fitnudge"
                  data-testid="sidebar-logo"
                  className="h-6 w-auto dark:hidden"
                  draggable={false}
                />
                <img
                  src="/logo-dark.png"
                  alt="Fitnudge"
                  className="h-6 w-auto hidden dark:block"
                  draggable={false}
                />
              </>
            )}
          </Link>
          {!collapsed && (
            <button
              data-testid="sidebar-collapse-btn"
              onClick={() => setCollapsed(true)}
              className="p-1.5 rounded-md text-muted-foreground/70 hover:text-foreground hover:bg-muted transition-colors"
              aria-label="Collapse sidebar"
            >
              <PanelLeftClose className="w-4 h-4" />
            </button>
          )}
        </div>

        {collapsed && (
          <button
            data-testid="sidebar-expand-btn"
            onClick={() => setCollapsed(false)}
            className="mx-auto mt-3 mb-1 p-1.5 rounded-md text-muted-foreground/70 hover:text-foreground hover:bg-muted transition-colors"
            aria-label="Expand sidebar"
          >
            <PanelLeftOpen className="w-4 h-4" />
          </button>
        )}

        {/* Grouped sections */}
        <nav className={`flex-1 overflow-y-auto py-4 ${collapsed ? "px-2" : "px-3"}`}>
          {navSections.map((section, si) => (
            <div key={si} className={si > 0 ? "mt-5" : ""}>
              {section.label && !collapsed && (
                <p
                  className="text-[10.5px] font-semibold uppercase tracking-[0.16em] text-muted-foreground/55 px-2.5 mb-2"
                  data-testid={`nav-section-${section.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {section.label}
                </p>
              )}
              {section.label && collapsed && si > 0 && (
                <div className="border-t border-border/40 my-2.5 mx-3" />
              )}
              <div className="space-y-0.5">
                {section.items.map(item => <NavItem key={item.to} {...item} />)}
              </div>
            </div>
          ))}
        </nav>

        {/* Footer block: Demo / Settings / Sign out */}
        <div className={`border-t border-border py-3 space-y-1 ${collapsed ? "px-2" : "px-3"}`}>
          {showDemoLoader && (
            <FooterButton
              icon={Sparkles}
              label={seeding ? "Loading…" : "Load demo data"}
              onClick={seedDemo}
              disabled={seeding}
              testid="seed-demo-btn"
            />
          )}
          <FooterLink to="/settings" icon={SettingsIcon} label="Settings" testid="nav-settings" />
          {user?.is_super_admin && (
            <FooterLink to="/admin" icon={ShieldCheck} label="Super Admin" testid="nav-admin" />
          )}
          {isDemo ? (
            <FooterButton
              icon={LogIn}
              label="Login"
              onClick={() => requireLogin("Login to use your own data")}
              testid="sidebar-login-btn"
            />
          ) : (
            <FooterButton
              icon={LogOut}
              label="Sign out"
              onClick={logout}
              testid="sidebar-logout-btn"
              tone="danger"
            />
          )}
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="lg:hidden sticky top-0 z-30 bg-sidebar border-b border-border h-14 flex items-center justify-between px-4">
        <Link to="/dashboard" className="flex items-center">
          <img src="/logo-light.png" alt="Fitnudge" data-testid="mobile-logo" className="h-5 w-auto dark:hidden" draggable={false} />
          <img src="/logo-dark.png" alt="Fitnudge" className="h-5 w-auto hidden dark:block" draggable={false} />
        </Link>
        <div className="flex items-center gap-1">
          <button
            onClick={toggleTheme}
            data-testid="mobile-theme-toggle"
            className="p-2 text-foreground/85"
            aria-label="Toggle theme"
          >
            {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </button>
          <button
            onClick={() => setMobileOpen(true)}
            data-testid="mobile-menu-btn"
            className="p-2 text-foreground/85"
            aria-label="Menu"
          >
            <Menu className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Mobile sheet */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-40">
          <div className="absolute inset-0 bg-foreground/20" onClick={() => setMobileOpen(false)} />
          <div className="absolute top-0 right-0 w-72 h-full bg-sidebar border-l border-border flex flex-col">
            <div className="h-14 flex items-center justify-between px-4 border-b border-border">
              <span className="font-display font-bold">Menu</span>
              <button onClick={() => setMobileOpen(false)} className="p-2"><X className="w-5 h-5" /></button>
            </div>
            <nav className="flex-1 p-3 space-y-3 overflow-y-auto">
              {navSections.map((s, i) => (
                <div key={i}>
                  {s.label && <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/60 px-2 mb-1">{s.label}</p>}
                  <div className="space-y-1">
                    {s.items.map(({ to, label, icon: Icon }) => (
                      <NavLink
                        key={to}
                        to={to}
                        onClick={() => setMobileOpen(false)}
                        className={({ isActive }) =>
                          `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium ${
                            isActive ? "bg-primary text-primary-foreground" : "text-foreground/85 hover:bg-muted"
                          }`
                        }
                      >
                        <Icon className="w-4 h-4" />
                        {label}
                      </NavLink>
                    ))}
                  </div>
                </div>
              ))}
            </nav>
            <div className="p-3 border-t border-border space-y-1">
              {showDemoLoader && (
                <button onClick={seedDemo} className="w-full text-left flex items-center gap-2 px-3 py-2 rounded-lg text-xs text-muted-foreground hover:bg-muted/50">
                  <Sparkles className="w-3.5 h-3.5" /> Load demo data
                </button>
              )}
              <NavLink to="/settings" onClick={() => setMobileOpen(false)} className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-foreground/85 hover:bg-muted">
                <SettingsIcon className="w-4 h-4" /> Settings
              </NavLink>
              {user?.is_super_admin && (
                <NavLink
                  to="/admin"
                  onClick={() => setMobileOpen(false)}
                  data-testid="mobile-nav-admin"
                  className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-foreground/85 hover:bg-muted"
                >
                  <ShieldCheck className="w-4 h-4" /> Super Admin
                </NavLink>
              )}
              <button
                onClick={isDemo ? () => { setMobileOpen(false); requireLogin("Login to use your own data"); } : logout}
                className={`w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium ${
                  isDemo
                    ? "text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-950/30"
                    : "text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/30"
                }`}
              >
                {isDemo ? <LogIn className="w-4 h-4" /> : <LogOut className="w-4 h-4" />}
                {isDemo ? "Login" : "Sign out"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main content */}
      <main className="flex-1 min-w-0 pb-20 lg:pb-0 page-fade-in relative">
        {/* Desktop top-right header */}
        <div className="hidden lg:flex absolute top-0 right-0 z-20 items-center gap-2 p-4" ref={profileRef}>
          <button
            onClick={toggleTheme}
            data-testid="theme-toggle-btn"
            className="p-2 rounded-md text-muted-foreground/80 hover:text-foreground hover:bg-muted transition-colors"
            aria-label="Toggle theme"
            title={theme === "dark" ? "Switch to light" : "Switch to dark"}
          >
            {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </button>
          <Link
            to="/settings"
            data-testid="header-settings-btn"
            className="p-2 rounded-md text-muted-foreground/80 hover:text-foreground hover:bg-muted transition-colors"
            aria-label="Settings"
            title="Settings"
          >
            <SettingsIcon className="w-4 h-4" />
          </Link>
          <button
            onClick={() => setProfileOpen(v => !v)}
            data-testid="profile-btn"
            className="flex items-center gap-2 pl-1 pr-2 py-1 rounded-full border border-border bg-card hover:bg-muted/50 transition-colors"
            aria-label="Profile menu"
            aria-expanded={profileOpen}
          >
            {user?.picture ? (
              <img src={user.picture} alt="" className="w-7 h-7 rounded-full" />
            ) : (
              <div className="w-7 h-7 rounded-full bg-primary/15 flex items-center justify-center text-xs font-semibold text-primary">
                {user?.name?.[0] || "U"}
              </div>
            )}
            <span className="text-xs font-medium text-foreground max-w-[100px] truncate">{user?.name || "Profile"}</span>
          </button>
          {profileOpen && (
            <div
              data-testid="profile-dropdown"
              className="absolute right-4 top-14 w-64 rounded-xl border border-border bg-card overflow-hidden"
            >
              <div className="px-4 py-3 border-b border-border">
                <div className="text-sm font-semibold text-foreground truncate">{user?.name}</div>
                <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
              </div>
              <div className="py-1">
                <Link
                  to="/settings"
                  onClick={() => setProfileOpen(false)}
                  className="flex items-center gap-2 px-4 py-2 text-sm text-foreground/85 hover:bg-muted"
                >
                  <SettingsIcon className="w-4 h-4" /> Account settings
                </Link>
                <button
                  onClick={() => { setProfileOpen(false); toggleTheme(); }}
                  className="w-full flex items-center gap-2 px-4 py-2 text-sm text-foreground/85 hover:bg-muted"
                >
                  {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
                  Switch to {theme === "dark" ? "light" : "dark"} mode
                </button>
                {isDemo ? (
                  <button
                    onClick={() => { setProfileOpen(false); requireLogin("Login to use your own data"); }}
                    data-testid="profile-login-btn"
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-950/30 font-medium"
                  >
                    <LogIn className="w-4 h-4" /> Login to your account
                  </button>
                ) : (
                  <button
                    onClick={logout}
                    data-testid="profile-logout-btn"
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/30"
                  >
                    <LogOut className="w-4 h-4" /> Log out
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        {children}
      </main>

      {/* Mobile bottom tab bar */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-30 bg-sidebar border-t border-border flex justify-around h-16">
        {mobileNav.map(({ to, label, icon: Icon, testid }) => (
          <NavLink
            key={to}
            to={to}
            data-testid={`mobile-${testid}`}
            className={({ isActive }) =>
              `flex-1 flex flex-col items-center justify-center gap-0.5 text-[11px] font-medium ${
                isActive ? "text-primary" : "text-muted-foreground/70"
              }`
            }
          >
            <Icon className="w-5 h-5" />
            {label}
          </NavLink>
        ))}
      </nav>
      </div>
    </div>
  );
}
